import {
  AcoFormData,
  AdgroupFormData,
  CampaignFormData,
  CreativeNameGenerateMode,
  FullNormalFormData,
  NormalCreative,
} from '@tt4b/ttam-enums';
import { useCreativeActionTypeIs, useCreativeIs, useStdCreativeAdgroupIs, ValidationError } from '@tt4b/ttam-hooks';
import { computed, ref, Ref } from '@vue/composition-api';

export function useAdNameIs(opt: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  creativeData: Ref<Partial<FullNormalFormData & AcoFormData>>;
  normalCreativeData: Ref<NormalCreative>;
  createMode: Ref<string>;
}) {
  const { campaignData, adgroupData, creativeData, createMode, normalCreativeData } = opt;

  const { isAco, isSupportNormalAd, isStandardAdsWithCatalog, isStandardAdsWithShowcase, isCLA, isPSA } =
    useStdCreativeAdgroupIs({
      adgroupFormData: adgroupData,
      campaignFormData: campaignData,
    });
  const { isEdit, isExistAdgroup } = useCreativeActionTypeIs({
    createMode,
    adgroupFormData: adgroupData,
  });

  const { isNative } = useCreativeIs({
    createMode,
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
    normalCreativeData,
  });
  // const { isControlGroup } = useSplitTestTool();
  const isControlGroup = ref(false);
  const allowModelList = computed(() => {
    // 编辑 ACO 创意，不允许修改广告名称
    // - 程序化创意的修改，对于某个创意来说逻辑一直都是先删再增，更新程序化创意名字会把下面所有的已经删除的creative名字都更新
    // - 如果一个程序化创意，修改过很多次，叉乘过很多次，那么他下面就有可能有成千上万个已删除的创意
    // - 如果涉及到名字模式切换，需要把这些历史已经删除的creative名字也都删掉，对于后端来说，会造成访问DB的一次事务超级大，这种情况是一定要避免的
    const isAcoNotAllowChangeMode = isAco.value && (isEdit.value || isExistAdgroup.value);
    if (isAcoNotAllowChangeMode) {
      return creativeData.value.creativeName
        ? [CreativeNameGenerateMode.Input]
        : [CreativeNameGenerateMode.MaterialAndInput];
    }
    if (isStandardAdsWithCatalog.value || isStandardAdsWithShowcase.value || isCLA.value || isPSA.value) {
      return [CreativeNameGenerateMode.Input];
    }
    if (isNative.value || !isSupportNormalAd.value) {
      return [CreativeNameGenerateMode.Input];
    }
    return [CreativeNameGenerateMode.Input, CreativeNameGenerateMode.MaterialAndInput];
  });
  const isAllowUseGenerate = computed(() => allowModelList.value.includes(CreativeNameGenerateMode.MaterialAndInput));
  const isAllowCloseGenerate = computed(
    () =>
      !(
        allowModelList.value.length === 1 && allowModelList.value.includes(CreativeNameGenerateMode.MaterialAndInput)
      ) && !isControlGroup.value,
  );
  const isAllowUseInput = computed(
    () => !(isAco.value && creativeData.value.creativeNameModel === CreativeNameGenerateMode.MaterialAndInput),
  );
  const isCurrentUsedMaterial = computed(
    () =>
      isAllowUseGenerate.value && creativeData.value.creativeNameModel === CreativeNameGenerateMode.MaterialAndInput,
  );
  const isDisabledUseGenerateBtn = computed(() => {
    // 在已有adgroup下新建aco，业务上认为是编辑
    const isAcoEdit = isEdit.value || isExistAdgroup.value;
    if (isAco.value && isAcoEdit) {
      return true;
    } else {
      return false;
    }
  });
  const isAllowChange = (mode: CreativeNameGenerateMode) => allowModelList.value.includes(mode);

  return {
    isAco,
    /** 是否允许创意名称输入 */
    isAllowUseInput,
    /** 是否允许使用素材辅助工具 */
    isAllowUseGenerate,
    /** 是否可以关闭素材名称 */
    isAllowCloseGenerate,
    /** 是否已选择素材名称作为创意名称 */
    isCurrentUsedMaterial,
    /** ACO下切换自定义命名和自动素材命名的方式 */
    isDisabledUseGenerateBtn,
    isAllowChange,
  };
}
